import { Ban, Check, CheckCheck, KeyRound, LockOpen, Megaphone, Send, Upload, X, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import type { Employee, Item } from '../../domain/models';
import { awaitsManualResult, can, needsDecision, publishesOnApproval } from '../../domain/models';
import { manualText } from '../../domain/payload';
import { useLocale, useT } from '../../l10n/i18n';
import { nameDative } from '../core/presentation';
import { useHeightVar } from '../core/useHeightVar';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { outside, safeUrl } from './links';
import { approveLabel, manualPlan } from './logic';

// The buttons for what the person may do with the item now: exactly what `item.actions` lists
// (ADR 0008, rule 1), laid out as the mobile app's _DecisionBar.

export interface DecisionHandlers {
  approve: () => void;
  reject: () => void;
  /** Spam for a screening item: rejected without a reason. */
  spam: () => void;
  requestChanges: () => void;
  discardEdits: () => void;
  /** A "Galima kurti" report: hand the work on, then mark the report seen. */
  handOffThenSeen: () => void;
  /** Copies the text a person pastes where the work goes. */
  copyForPaste: (text: string, done: string) => void;
  published: () => void;
}

/** The person may approve, reject or send it back now. */
const canDecide = (i: Item) => can(i, 'approve') || can(i, 'reject') || can(i, 'request_changes');

function approveIcon(item: Item): LucideIcon {
  if (item.kind === 'access_request') return KeyRound;
  if (publishesOnApproval(item)) return Megaphone;
  if (item.kind === 'ad') return Check;
  return item.outward ? Send : Check;
}

/** A button's words on one line; a long label ends in "…" instead of wrapping. */
const Label = ({ children }: { children: ReactNode }) => <span className="iv-label">{children}</span>;

export function DecisionBar({
  item,
  editing,
  handoffTargets,
  isViewer,
  busy,
  on,
}: {
  item: Item;
  editing: boolean;
  /** Who could take the work; empty when it cannot be handed off. */
  handoffTargets: Employee[];
  isViewer: boolean;
  busy: boolean;
  on: DecisionHandlers;
}) {
  const t = useT();
  // Toasts rise above the bar, however tall it is (theme.css).
  const barRef = useHeightVar('--bar-lift');
  const locale = useLocale();
  const ApproveIcon = approveIcon(item);
  const approveButton = (cls: string) => (
    <button type="button" className={`btn btn-filled ${cls}`} onClick={on.approve} data-testid="decide-approve">
      <ApproveIcon size={18} aria-hidden />
      <Label>{approveLabel(t, item)}</Label>
    </button>
  );

  let content: ReactNode = null;
  if (editing && can(item, 'edit')) {
    content = (
      <>
        <button type="button" className="btn btn-outlined iv-f1" onClick={on.discardEdits}>
          <Label>{t('actionDiscardEdits')}</Label>
        </button>
        {can(item, 'approve') && approveButton('iv-f1')}
      </>
    );
  } else if (canDecide(item)) {
    const first = handoffTargets[0];
    if (item.type === 'screening') {
      content = (
        <>
          {can(item, 'reject') && (
            <button type="button" className="btn btn-outlined iv-danger iv-f2" onClick={on.spam}>
              <Ban size={18} aria-hidden />
              <Label>{t('actionSpam')}</Label>
            </button>
          )}
          {can(item, 'approve') && (
            <button type="button" className="btn btn-filled iv-f3" onClick={on.approve} data-testid="decide-approve">
              <LockOpen size={18} aria-hidden />
              <Label>{t('actionLetIn')}</Label>
            </button>
          )}
        </>
      );
    } else if (
      item.type === 'report' &&
      can(item, 'approve') &&
      can(item, 'hand_off') &&
      first &&
      item.payload.type === 'report' &&
      item.payload.suggestHandoff
    ) {
      // A report whose work can start now ("Galima kurti") offers the handoff as the main action.
      content = (
        <>
          <button type="button" className="btn btn-outlined iv-f2" onClick={on.approve} data-testid="decide-approve">
            <Label>{t('actionSeen')}</Label>
          </button>
          <button type="button" className="btn btn-filled iv-f5" onClick={on.handOffThenSeen}>
            <EmployeeAvatar avatar={first.avatar} color={first.color} size={24} />
            <Label>{t('handoffTo', { name: nameDative(locale, first.name) })}</Label>
          </button>
        </>
      );
    } else if (item.type === 'report' && can(item, 'approve')) {
      content = (
        <button type="button" className="btn btn-filled iv-f1" onClick={on.approve} data-testid="decide-approve">
          <CheckCheck size={18} aria-hidden />
          <Label>{t('actionSeen')}</Label>
        </button>
      );
    } else {
      content = (
        <>
          {can(item, 'reject') && (
            <button
              type="button"
              className="btn btn-outlined btn-square iv-danger"
              onClick={on.reject}
              aria-label={t('actionReject')}
              title={t('actionReject')}
            >
              <X size={22} aria-hidden />
            </button>
          )}
          {can(item, 'request_changes') && (
            <button type="button" className="btn btn-outlined iv-f5" onClick={on.requestChanges} data-tour="request-changes">
              <Label>{t('actionRequestChanges')}</Label>
            </button>
          )}
          {can(item, 'approve') && approveButton('iv-f7')}
        </>
      );
    }
  } else if (can(item, 'report_result')) {
    const text = manualText(item.payload);
    const plan = manualPlan(t, item);
    const target = safeUrl(plan.target);
    const OpenIcon = plan.icon;
    const DoneIcon = plan.message ? CheckCheck : Upload;
    // Copying also opens where it goes (the group, a ready email), so the person only pastes.
    const copyOpen =
      text === undefined ? null : target ? (
        <a className="btn btn-outlined iv-f1" {...outside(target)} onClick={() => on.copyForPaste(text, plan.done)}>
          <OpenIcon size={18} aria-hidden />
          <Label>{plan.open}</Label>
        </a>
      ) : (
        <button type="button" className="btn btn-outlined iv-f1" onClick={() => on.copyForPaste(text, plan.done)}>
          <OpenIcon size={18} aria-hidden />
          <Label>{plan.open}</Label>
        </button>
      );
    content = (
      <>
        {copyOpen}
        <button type="button" className="btn btn-filled iv-f1" onClick={on.published}>
          <DoneIcon size={18} aria-hidden />
          <Label>{plan.done}</Label>
        </button>
      </>
    );
  } else if (isViewer && (needsDecision(item) || awaitsManualResult(item))) {
    // A viewer is told why work that waits for someone has no buttons here.
    return (
      <div ref={barRef} className="iv-bar">
        <p className="body-small iv-center iv-inner">{t('viewerNotice')}</p>
      </div>
    );
  }

  if (content === null) return null;
  return (
    <div ref={barRef} className="iv-bar">
      <div className="iv-inner">
        <fieldset className="iv-actions" disabled={busy} aria-busy={busy}>
          {content}
        </fieldset>
      </div>
    </div>
  );
}
